import { useEffect, useState, Fragment } from "react";
import { useLang, SUPPORTED_LANGS } from "../../i18n";
import logo from "../../assets/logo-eskale.png";

function LangToggle({ scrolled, className = "" }) {
  const { lang, setLang } = useLang();
  return (
    <div className={`flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] ${className}`}>
      {SUPPORTED_LANGS.map((l, i) => (
        <Fragment key={l}>
          {i > 0 && <span className={scrolled ? "text-foreground/30" : "text-ivory/40"}>|</span>}
          <button
            type="button"
            onClick={() => setLang(l)}
            data-testid={`lang-toggle-${l}`}
            className={`px-1.5 py-1 transition-colors ${
              lang === l ? "text-gold" : scrolled ? "text-foreground/60 hover:text-gold" : "text-ivory/70 hover:text-gold"
            }`}
          >
            {l.toUpperCase()}
          </button>
        </Fragment>
      ))}
    </div>
  );
}

export function Nav({ onBook }) {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-40 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-ivory/85 border-b border-border"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-4 sm:px-6 lg:px-10">
        <a href="#top" className="flex shrink-0 items-center gap-3">
          <img
            src={logo}
            alt="L'Eskale Guyane"
            className={`h-10 w-10 object-contain transition-all ${scrolled ? "" : "drop-shadow-[0_2px_8px_rgba(0,0,0,0.4)]"}`}
          />
          <span className="hidden xl:flex items-baseline gap-2">
            <span className={`font-display italic text-2xl ${scrolled ? "text-foreground" : "text-ivory"}`}>L&rsquo;Eskale</span>
            <span className="text-[10px] uppercase tracking-[0.3em] text-gold">Guyane</span>
          </span>
        </a>

        <nav className="hidden items-center gap-3 md:flex lg:gap-6 xl:gap-8">
          {t.nav.links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className={`whitespace-nowrap text-[10px] uppercase tracking-[0.18em] transition-colors hover:text-gold lg:text-[11px] lg:tracking-[0.22em] ${
                scrolled ? "text-foreground/80" : "text-ivory/90"
              }`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden shrink-0 items-center gap-3 md:flex">
          <LangToggle scrolled={scrolled} />
          <button
            type="button"
            onClick={onBook}
            className="inline-flex items-center bg-gold px-4 py-2.5 text-[10px] uppercase tracking-[0.18em] text-primary-foreground transition-all hover:brightness-95 lg:px-5 lg:text-[11px] lg:tracking-[0.22em]"
          >
            {t.nav.book}
          </button>
        </div>

        <div className="flex items-center gap-1 md:hidden">
          <LangToggle scrolled={scrolled} />
          <button
            aria-label="Menu"
            onClick={() => setOpen((v) => !v)}
            className="flex flex-col gap-1.5 p-2"
          >
            <span className={`h-px w-6 transition-transform ${scrolled ? "bg-foreground" : "bg-ivory"} ${open ? "translate-y-2 rotate-45" : ""}`} />
            <span className={`h-px w-6 transition-opacity ${scrolled ? "bg-foreground" : "bg-ivory"} ${open ? "opacity-0" : ""}`} />
            <span className={`h-px w-6 transition-transform ${scrolled ? "bg-foreground" : "bg-ivory"} ${open ? "-translate-y-2 -rotate-45" : ""}`} />
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-border bg-background/95 backdrop-blur-xl">
          <nav className="flex flex-col px-6 py-4">
            {t.nav.links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="py-3 text-sm uppercase tracking-[0.22em] text-foreground/80 hover:text-gold"
              >
                {l.label}
              </a>
            ))}
            <button
              type="button"
              onClick={() => { setOpen(false); onBook(); }}
              className="mt-3 inline-flex items-center justify-center bg-gold px-5 py-3 text-xs uppercase tracking-[0.22em] text-primary-foreground"
            >
              {t.nav.book}
            </button>
          </nav>
        </div>
      )}
    </header>
  );
}
